import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Col,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import { CreateItemTypeFromJiraWizard } from './CreateItemTypeFromJiraWizard.tsx';
import { AddFieldsFromJiraWizard } from './AddFieldsFromJiraWizard.tsx';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnsType } from 'antd/es/table';
import type {
  CreateFieldRequest,
  CreateItemTypeRequest,
  FieldDefinition,
  FieldType,
  ItemType,
  ItemTypeListItem,
  SelectOption,
} from '@lens/shared';
import { TEXT_COMPATIBLE_FIELD_TYPES, TITLE_CAPABLE_FIELD_TYPES } from '@lens/shared';
// M7: computed fields now supported alongside M4 types.
const SUPPORTED_FIELD_TYPES = [
  'text', 'textarea', 'markdown', 'number', 'select', 'multi_select',
  'date', 'reference', 'user', 'url', 'boolean', 'computed',
] as const;
import {
  createField,
  createItemType,
  deleteField,
  deleteItemType,
  duplicateItemType,
  getItemType,
  listItemTypes,
  reorderFields,
  updateField,
  updateItemType,
} from './api.ts';
import { listDictionaries } from '../dictionaries/api.ts';
import { suggestFormula, explainFormula } from '../ai/api.ts';
import { radius, spacing } from '../../shared/theme/tokens.ts';
import { ApiError } from '../../shared/api/client.ts';
import { slugify } from '../../shared/lib/slugify.ts';
import { LinkRulesPanel } from '../link-rules/LinkRulesPanel.tsx';
import { EmojiPicker } from '../../shared/components/EmojiPicker.tsx';
import type { DictionaryListItem } from '@lens/shared';

const { Title, Text } = Typography;

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Текст',
  textarea: 'Многострочный текст',
  markdown: 'Markdown',
  number: 'Число',
  select: 'Выбор (одно значение)',
  multi_select: 'Выбор (несколько)',
  date: 'Дата',
  reference: 'Ссылка на справочник',
  user: 'Пользователь',
  url: 'URL',
  boolean: 'Да/Нет',
  computed: 'Вычисляемое',
};

const FIELD_TYPE_SELECT_OPTIONS = [
  {
    label: 'Текст',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => ['text', 'textarea', 'markdown'].includes(type))
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Число',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => type === 'number')
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Дата/время',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => type === 'date')
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Выбор',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => ['select', 'multi_select'].includes(type))
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Связи',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => type === 'reference' || type === 'user')
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Системные',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => type === 'computed')
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
  {
    label: 'Прочее',
    options: SUPPORTED_FIELD_TYPES
      .filter((type) => ['boolean', 'url'].includes(type))
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type })),
  },
].filter((group) => group.options.length > 0);

// Плоский список текстовых типов — единственные, между которыми разрешена
// смена типа у существующего поля (значения лежат в одной колонке value_text).
const TEXT_COMPATIBLE_SELECT_OPTIONS = [
  {
    label: 'Текст',
    options: TEXT_COMPATIBLE_FIELD_TYPES.map((type) => ({
      value: type,
      label: FIELD_TYPE_LABELS[type] ?? type,
    })),
  },
];

const isTextCompatibleType = (type: string): boolean =>
  (TEXT_COMPATIBLE_FIELD_TYPES as readonly string[]).includes(type);

const STATUS_CATEGORY_LABELS: Record<string, string> = {
  backlog: 'Бэклог',
  active: 'Активно',
  done: 'Готово',
  cancelled: 'Отменено',
};

// ─── Sortable row for field drag-drop ─────────────────────────────────────

function SortableFieldRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: string }) {
  const id = props['data-row-key'] ?? '';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform ? { ...transform, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'default',
  };

  // Only apply drag listeners to the HolderOutlined handle cell (first td)
  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
}

interface ItemTypesTabProps {
  spaceId: string;
  myRole: 'admin' | 'member' | 'viewer';
  /** Включена ли Jira-интеграция в пространстве — гейтит «Из Jira»-кнопки. */
  jiraEnabled?: boolean;
}

// ─── CreateItemTypeModal ──────────────────────────────────────────────────────

interface CreateItemTypeModalProps {
  spaceId: string;
  open: boolean;
  onCancel: () => void;
  onCreated: (it: ItemType) => void;
}

function CreateItemTypeModal({ spaceId, open, onCancel, onCreated }: CreateItemTypeModalProps): JSX.Element {
  const [form] = Form.useForm<CreateItemTypeRequest>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const nameValue = Form.useWatch('name', form);

  // Сброс состояния при каждом открытии — иначе ошибка/значения от прошлой
  // попытки протекают в свежую модалку.
  useEffect(() => {
    if (open) {
      form.resetFields();
      setError(null);
      setSaving(false);
      setSlugTouched(false);
    }
  }, [open, form]);

  // Автозаполнение slug из названия, пока пользователь не правил slug руками.
  // Разделитель — underscore: бэкенд для slug типа записи принимает только
  // [a-z][a-z0-9_] (см. packages/shared item-types schema), дефис недопустим.
  useEffect(() => {
    if (!open || slugTouched) return;
    form.setFieldValue('slug', slugify(nameValue ?? '', '_'));
  }, [open, slugTouched, nameValue, form]);

  const onOk = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    setError(null);
    try {
      const it = await createItemType(spaceId, values);
      message.success('Тип записи создан');
      form.resetFields();
      onCreated(it);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'slug_conflict') {
        form.setFields([{ name: 'slug', errors: [e.message || 'Тип с таким slug уже существует'] }]);
      } else {
        setError(e instanceof Error ? e.message : 'Ошибка при создании');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Новый тип записи"
      open={open}
      onCancel={() => { form.resetFields(); onCancel(); }}
      onOk={onOk}
      okText="Создать"
      cancelText="Отмена"
      confirmLoading={saving}
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
          <Input placeholder="Личная цель" />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Slug"
          rules={[
            { required: true, message: 'Введите slug' },
            { pattern: /^[a-z][a-z0-9_]{0,49}$/, message: 'Строчные буквы, цифры и underscore' },
          ]}
        >
          <Input placeholder="personal_goal" onChange={() => setSlugTouched(true)} />
        </Form.Item>
        <Form.Item name="icon" label="Иконка" valuePropName="value">
          <EmojiPicker size="middle" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── LensyFormulaChat ─────────────────────────────────────────────────────────

interface LensyFormulaChatMessage {
  role: 'user' | 'assistant';
  content: string;
  formula?: string;
  kind?: 'suggest' | 'explain' | 'error';
}

interface LensyFormulaChatProps {
  messages: LensyFormulaChatMessage[];
  input: string;
  loading: boolean;
  onInputChange: (v: string) => void;
  onSend: () => void | Promise<void>;
  onExplain: () => void | Promise<void>;
  onClear: () => void;
}

function LensyFormulaChat({
  messages, input, loading, onInputChange, onSend, onExplain, onClear,
}: LensyFormulaChatProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  return (
    <div
      style={{
        border: `1px solid var(--color-border)`,
        borderRadius: radius.md,
        background: 'var(--color-bg-layout)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        ref={scrollRef}
        style={{
          maxHeight: 240,
          minHeight: 96,
          overflowY: 'auto',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {messages.length === 0 && !loading && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Опишите формулу или изменение — Lensy запомнит контекст разговора.
            Например: «округли до целого», затем «и добавь множитель {'{вес}'}».
          </Typography.Text>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const isError = m.kind === 'error';
          const bg = isUser ? 'var(--color-primary-bg)' : isError ? 'var(--color-error-bg)' : 'var(--color-bg-container)';
          const border = isUser ? 'var(--color-primary)' : isError ? 'var(--color-error)' : 'var(--color-border)';
          return (
            <div
              key={i}
              style={{
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: radius.md,
                padding: '6px 10px',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Typography.Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {isUser ? 'Вы' : 'Lensy'}
              </Typography.Text>
              <div>{m.content}</div>
              {m.formula && (
                <div
                  style={{
                    marginTop: 4,
                    padding: '4px 6px',
                    background: 'var(--color-bg-layout)',
                    borderRadius: 4,
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {m.formula}
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Lensy думает…
          </div>
        )}
      </div>
      <div style={{ borderTop: `1px solid var(--color-border)`, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input.TextArea
            placeholder="Опишите формулу или изменение"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
          <Button
            type="primary"
            icon={<AiSparkIcon />}
            loading={loading}
            disabled={!input.trim() || loading}
            onClick={() => void onSend()}
          >
            Отправить
          </Button>
        </Space.Compact>
        <Space size={4}>
          <Button
            size="small"
            type="link"
            icon={<AiSparkIcon />}
            disabled={loading}
            style={{ paddingLeft: 0 }}
            onClick={() => void onExplain()}
          >
            Объяснить формулу
          </Button>
          {messages.length > 0 && (
            <Button
              size="small"
              type="link"
              disabled={loading}
              onClick={onClear}
            >
              Очистить чат
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}

// ─── FieldBuilderModal ────────────────────────────────────────────────────────

interface FieldBuilderModalProps {
  spaceId: string;
  itemTypeId: string;
  open: boolean;
  editingField: FieldDefinition | null;
  existingFields: FieldDefinition[];
  onCancel: () => void;
  onSaved: (field: FieldDefinition) => void;
}

interface SelectOptionRow {
  value: string;
  label: string;
  color?: string;
  category?: string;
  /** Стабильный ключ строки в редакторе (для перетаскивания); в конфиг не сохраняется. */
  _key: string;
}

let optionRowSeq = 0;
const nextOptionKey = () => `opt-${Date.now()}-${optionRowSeq++}`;
const withOptionKeys = (opts: Omit<SelectOptionRow, '_key'>[]): SelectOptionRow[] =>
  opts.map((o) => ({ ...o, _key: nextOptionKey() }));
const stripOptionKeys = (opts: SelectOptionRow[]): SelectOption[] =>
  opts.map(({ _key: _k, ...o }) => o as SelectOption);

/** Строка опции select с ручкой для перетаскивания — порядок опций задаёт порядок сортировки и группировки. */
function SortableOptionRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform ? { ...transform, scaleY: 1 } : null),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Row gutter={8} style={{ marginBottom: 4 }} align="middle">
        <Col span={1} style={{ textAlign: 'center' }}>
          <span
            {...attributes}
            {...listeners}
            aria-label="Перетащить опцию"
            style={{ cursor: 'grab', color: 'var(--color-text-secondary)', touchAction: 'none' }}
          >
            <HolderOutlined />
          </span>
        </Col>
        {children}
      </Row>
    </div>
  );
}

function FieldBuilderModal({
  spaceId,
  itemTypeId,
  open,
  editingField,
  existingFields,
  onCancel,
  onSaved,
}: FieldBuilderModalProps): JSX.Element {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldType, setFieldType] = useState<FieldType>('text');
  const [hasCategories, setHasCategories] = useState(false);
  const [isTitle, setIsTitle] = useState(false);
  const [options, setOptions] = useState<SelectOptionRow[]>([]);
  const optionDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [slugTouched, setSlugTouched] = useState(false);
  const nameValue = Form.useWatch('name', form);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  type LensyChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    formula?: string;
    kind?: 'suggest' | 'explain' | 'error';
  };
  const [chatMessages, setChatMessages] = useState<LensyChatMessage[]>([]);
  const [initialFormula, setInitialFormula] = useState<string>('');
  const [dictionaries, setDictionaries] = useState<DictionaryListItem[]>([]);

  useEffect(() => {
    if (fieldType === 'reference') {
      listDictionaries().then(setDictionaries).catch(() => {});
    }
  }, [fieldType]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    setSlugTouched(false);
    setChatMessages([]);
    setAiPrompt('');
    const startFormula = editingField
      ? String((editingField.config as Record<string, unknown>)?.formula ?? '')
      : '';
    setInitialFormula(startFormula);
    if (editingField) {
      setFieldType(editingField.fieldType as FieldType);
      const cfg = editingField.config as Record<string, unknown>;
      setHasCategories(Boolean(cfg?.hasCategories));
      setIsTitle(Boolean(cfg?.isTitle));
      setOptions(withOptionKeys((cfg?.options as Omit<SelectOptionRow, '_key'>[]) ?? []));
      form.setFieldsValue({
        name: editingField.name,
        slug: editingField.slug,
        fieldType: editingField.fieldType,
        required: editingField.required,
        dictionaryId: cfg?.dictionaryId,
        multiple: cfg?.multiple ?? false,
        min: cfg?.min,
        max: cfg?.max,
        precision: cfg?.precision,
        includeTime: cfg?.includeTime ?? false,
        formula: cfg?.formula ?? '',
        defaultValue: cfg?.defaultValue,
        autofillCurrentUser: cfg?.autofillCurrentUser ?? false,
      });
    } else {
      form.resetFields();
      setFieldType('text');
      setHasCategories(false);
      setIsTitle(false);
      setOptions([]);
    }
  }, [open, editingField, form]);

  // Автозаполнение slug из названия при создании поля (при редактировании slug
  // иммутабелен и поле disabled). Разделитель — underscore: slug поля по
  // правилам валидации — [a-z][a-z0-9_].
  useEffect(() => {
    if (!open || editingField || slugTouched) return;
    form.setFieldValue('slug', slugify(nameValue ?? '', '_'));
  }, [open, editingField, slugTouched, nameValue, form]);

  const buildConfig = (): Record<string, unknown> => {
    const dv = form.getFieldValue('defaultValue');
    const defaultValue = (fieldType !== 'computed' && dv !== undefined && dv !== '' && dv !== null)
      ? dv
      : undefined;

    switch (fieldType) {
      case 'select':
      case 'multi_select':
        return { options: stripOptionKeys(options), hasCategories: fieldType === 'select' ? hasCategories : false, defaultValue };
      case 'reference':
        // reference тоже может быть полем-названием: пишем isTitle лишь когда
        // включён, чтобы не засорять config. В заголовке резолвится display
        // справочника (см. resolvedRefs), а не сырой entryId.
        return {
          dictionaryId: form.getFieldValue('dictionaryId'),
          multiple: form.getFieldValue('multiple') ?? false,
          defaultValue,
          ...(isTitle ? { isTitle: true } : {}),
          ...(form.getFieldValue('autofillCurrentUser') ? { autofillCurrentUser: true } : {}),
        };
      case 'user':
        return {
          multiple: form.getFieldValue('multiple') ?? false,
          defaultValue,
          ...(form.getFieldValue('autofillCurrentUser') ? { autofillCurrentUser: true } : {}),
        };
      case 'number':
        return {
          min: form.getFieldValue('min') ?? undefined,
          max: form.getFieldValue('max') ?? undefined,
          precision: form.getFieldValue('precision') ?? undefined,
          defaultValue,
        };
      case 'date':
        return { includeTime: form.getFieldValue('includeTime') ?? false, defaultValue };
      case 'computed':
        return { formula: form.getFieldValue('formula') ?? '' };
      default:
        // text/textarea/markdown/url. Признак «название» — только для текстовых
        // (не url): пишем isTitle лишь когда включён, чтобы не засорять config.
        return {
          defaultValue,
          ...(isTitle && TITLE_CAPABLE_FIELD_TYPES.includes(
            fieldType as (typeof TITLE_CAPABLE_FIELD_TYPES)[number],
          )
            ? { isTitle: true }
            : {}),
        };
    }
  };

  const onOk = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    setError(null);
    try {
      const payload: CreateFieldRequest = {
        name: values.name as string,
        slug: values.slug as string,
        fieldType: fieldType,
        required: values.required ?? false,
        config: buildConfig(),
      };

      let saved: FieldDefinition;
      if (editingField) {
        saved = await updateField(spaceId, itemTypeId, editingField.id, {
          name: payload.name,
          ...(fieldType !== editingField.fieldType ? { fieldType } : {}),
          config: payload.config,
          required: payload.required,
        });
      } else {
        saved = await createField(spaceId, itemTypeId, payload);
      }
      message.success(editingField ? 'Поле сохранено' : 'Поле создано');
      onSaved(saved);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'slug_conflict') {
        form.setFields([{ name: 'slug', errors: [e.message || 'Поле с таким slug уже существует'] }]);
      } else {
        setError(e instanceof Error ? e.message : 'Ошибка');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editingField ? 'Редактировать поле' : 'Новое поле'}
      open={open}
      onCancel={() => { form.resetFields(); onCancel(); }}
      onOk={onOk}
      okText={editingField ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={saving}
      width={600}
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label="Название" rules={[{ required: true }]}>
              <Input placeholder="Заголовок" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="slug"
              label="Slug"
              rules={[
                { required: true },
                { pattern: /^[a-z][a-z0-9_]{0,49}$/, message: 'Строчные буквы, цифры, underscore' },
              ]}
            >
              <Input placeholder="title" disabled={!!editingField} title={editingField ? 'Slug нельзя изменить после создания' : undefined} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Тип поля"
          help={
            editingField && isTextCompatibleType(editingField.fieldType)
              ? 'Тип можно сменить только между текстовыми типами — данные сохранятся'
              : editingField
                ? 'Тип нельзя изменить после создания'
                : undefined
          }
        >
          <Select
            value={fieldType}
            onChange={(v) => { setFieldType(v as FieldType); setOptions([]); setHasCategories(false); form.setFieldValue('defaultValue', undefined); }}
            disabled={!!editingField && !isTextCompatibleType(editingField.fieldType)}
            options={
              editingField && isTextCompatibleType(editingField.fieldType)
                ? TEXT_COMPATIBLE_SELECT_OPTIONS
                : FIELD_TYPE_SELECT_OPTIONS
            }
          />
        </Form.Item>

        {/* «Обязательное» бессмысленно для вычисляемого поля — значение считается на бэкенде */}
        {fieldType !== 'computed' && (
          <Form.Item name="required" valuePropName="checked">
            <Checkbox>Обязательное поле</Checkbox>
          </Form.Item>
        )}

        {/* Признак «название» — для текстовых полей и ссылки на справочник.
            Значение такого поля выносится в заголовок записи везде (карточка,
            грид, ссылки, канбан, поиск); для reference показывается display
            справочника, а не entryId. Не более одного на тип записи — бэкенд
            снимет флаг с предыдущего. */}
        {TITLE_CAPABLE_FIELD_TYPES.includes(
          fieldType as (typeof TITLE_CAPABLE_FIELD_TYPES)[number],
        ) && (
          <Form.Item
            style={{ marginTop: -8 }}
            help="Значение этого поля будет заголовком записи везде. У типа записи может быть только одно поле-название."
          >
            <Checkbox checked={isTitle} onChange={(e) => setIsTitle(e.target.checked)}>
              Использовать как название записи
            </Checkbox>
          </Form.Item>
        )}

        {/* select / multi_select — конструктор опций */}
        {(fieldType === 'select' || fieldType === 'multi_select') && (
          <>
            {fieldType === 'select' && (
              <Form.Item>
                <Checkbox
                  checked={hasCategories}
                  onChange={(e) => setHasCategories(e.target.checked)}
                >
                  Статусное поле (с категориями backlog / active / done / cancelled)
                </Checkbox>
              </Form.Item>
            )}
            <Form.Item
              label="Опции"
              tooltip="Порядок опций здесь задаёт порядок сортировки и группировки по этому полю. Перетаскивайте строки за ручку слева."
            >
              {options.length > 0 && (
                <Row gutter={8} style={{ marginBottom: 4 }} align="middle">
                  <Col span={1} />
                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Значение
                    </Text>
                  </Col>
                  <Col span={7}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Отображение
                    </Text>
                  </Col>
                  <Col span={3}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Цвет
                    </Text>
                  </Col>
                  {hasCategories && (
                    <Col span={5}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Категория
                      </Text>
                    </Col>
                  )}
                  <Col span={2} />
                </Row>
              )}
              <DndContext
                sensors={optionDndSensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }: DragEndEvent) => {
                  if (!over || active.id === over.id) return;
                  setOptions((prev) => {
                    const from = prev.findIndex((o) => o._key === active.id);
                    const to = prev.findIndex((o) => o._key === over.id);
                    return from < 0 || to < 0 ? prev : arrayMove(prev, from, to);
                  });
                }}
              >
              <SortableContext items={options.map((o) => o._key)} strategy={verticalListSortingStrategy}>
              {options.map((opt, i) => (
                <SortableOptionRow key={opt._key} id={opt._key}>
                  <Col span={6}>
                    <Input
                      size="small"
                      placeholder="Значение"
                      value={opt.value}
                      onChange={(e) =>
                        setOptions((prev) =>
                          prev.map((o, j) => (j === i ? { ...o, value: e.target.value } : o)),
                        )
                      }
                    />
                  </Col>
                  <Col span={7}>
                    <Input
                      size="small"
                      placeholder="Отображение"
                      value={opt.label}
                      onChange={(e) =>
                        setOptions((prev) =>
                          prev.map((o, j) => (j === i ? { ...o, label: e.target.value } : o)),
                        )
                      }
                    />
                  </Col>
                  <Col span={3}>
                    <ColorPicker
                      size="small"
                      value={opt.color ?? '#1677ff'}
                      onChange={(c) =>
                        setOptions((prev) =>
                          prev.map((o, j) => (j === i ? { ...o, color: c.toHexString() } : o)),
                        )
                      }
                      allowClear
                      onClear={() =>
                        setOptions((prev) =>
                          prev.map((o, j) => (j === i ? { ...o, color: undefined } : o)),
                        )
                      }
                      presets={[
                        {
                          label: 'Базовые',
                          colors: [
                            '#1677ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
                            '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#8c8c8c',
                          ],
                        },
                      ]}
                    />
                  </Col>
                  {hasCategories && (
                    <Col span={5}>
                      <Select
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Категория"
                        value={opt.category}
                        onChange={(v) =>
                          setOptions((prev) =>
                            prev.map((o, j) => (j === i ? { ...o, category: v as string } : o)),
                          )
                        }
                        options={Object.entries(STATUS_CATEGORY_LABELS).map(([v, l]) => ({
                          value: v,
                          label: l,
                        }))}
                      />
                    </Col>
                  )}
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))}
                    />
                  </Col>
                </SortableOptionRow>
              ))}
              </SortableContext>
              </DndContext>
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  setOptions((prev) => [...prev, { value: '', label: '', color: '#1677ff', _key: nextOptionKey() }])
                }
              >
                Добавить опцию
              </Button>
            </Form.Item>
          </>
        )}

        {/* reference */}
        {fieldType === 'reference' && (
          <>
            <Form.Item
              name="dictionaryId"
              label="Справочник"
              rules={[{ required: true, message: 'Выберите справочник' }]}
            >
              <Select
                placeholder="Выберите справочник"
                options={dictionaries.map((d) => ({ value: d.id, label: d.name }))}
              />
            </Form.Item>
            <Form.Item name="multiple" valuePropName="checked">
              <Checkbox>Множественный выбор</Checkbox>
            </Form.Item>
            <Form.Item
              name="autofillCurrentUser"
              valuePropName="checked"
              style={{ marginTop: -12 }}
              extra="Работает, если ключевая колонка справочника содержит логин или email пользователя"
            >
              <Checkbox>Подставлять текущего пользователя при создании записи</Checkbox>
            </Form.Item>
          </>
        )}

        {fieldType === 'user' && (
          <>
            <Form.Item name="multiple" valuePropName="checked">
              <Checkbox>Множественный выбор</Checkbox>
            </Form.Item>
            <Form.Item name="autofillCurrentUser" valuePropName="checked" style={{ marginTop: -12 }}>
              <Checkbox>Подставлять текущего пользователя при создании записи</Checkbox>
            </Form.Item>
          </>
        )}

        {/* number */}
        {fieldType === 'number' && (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="min" label="Мин.">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max" label="Макс.">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="precision" label="Знаки после запятой">
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* date */}
        {fieldType === 'date' && (
          <Form.Item name="includeTime" valuePropName="checked">
            <Checkbox>Включать время</Checkbox>
          </Form.Item>
        )}

        {/* Значение по умолчанию — для всех типов кроме computed */}
        {fieldType !== 'computed' && (
          <>
            {fieldType === 'text' || fieldType === 'textarea' || fieldType === 'markdown' || fieldType === 'url' ? (
              <Form.Item name="defaultValue" label="Значение по умолчанию">
                {fieldType === 'textarea' || fieldType === 'markdown' ? (
                  <Input.TextArea rows={2} placeholder="Текст по умолчанию" allowClear />
                ) : (
                  <Input placeholder="Значение по умолчанию" allowClear />
                )}
              </Form.Item>
            ) : fieldType === 'number' ? (
              <Form.Item name="defaultValue" label="Значение по умолчанию">
                <InputNumber style={{ width: '100%' }} placeholder="Число по умолчанию" />
              </Form.Item>
            ) : fieldType === 'boolean' ? (
              <Form.Item name="defaultValue" label="Значение по умолчанию">
                <Select
                  allowClear
                  placeholder="Не задано"
                  options={[
                    { value: true, label: 'Да' },
                    { value: false, label: 'Нет' },
                  ]}
                />
              </Form.Item>
            ) : fieldType === 'date' ? (
              <Form.Item name="defaultValue" label="Значение по умолчанию">
                <Input placeholder="ISO дата, напр. 2026-01-01" allowClear />
              </Form.Item>
            ) : (fieldType === 'select' || fieldType === 'multi_select') && options.length > 0 ? (
              <Form.Item name="defaultValue" label="Значение по умолчанию">
                <Select
                  allowClear
                  placeholder="Не задано"
                  mode={fieldType === 'multi_select' ? 'multiple' : undefined}
                  options={options.filter((o) => o.value).map((o) => ({ value: o.value, label: o.label || o.value }))}
                />
              </Form.Item>
            ) : null}
          </>
        )}

        {/* computed (M7) — formula editor with AI assist */}
        {fieldType === 'computed' && (
          <>
            <Form.Item
              name="formula"
              label="Формула"
              rules={[{ required: true, message: 'Введите формулу' }]}
              extra={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Ссылки на поля: {'{slug}'}, функции: IF, ROUND, MIN, MAX, ABS, SCALE, CONCAT, FORMAT_NUMBER, COALESCE.{' '}
                  Пример: CONCAT("Кат — ", {'{category}'}, ", Зрелость — ", {'{maturity}'})
                </Typography.Text>
              }
            >
              <Input.TextArea
                rows={3}
                placeholder="ROUND({category} * {maturity}, 2)"
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
            <Form.Item label="Чат с Lensy">
              <LensyFormulaChat
                messages={chatMessages}
                input={aiPrompt}
                onInputChange={setAiPrompt}
                loading={aiLoading}
                onSend={async () => {
                  const text = aiPrompt.trim();
                  if (!text || aiLoading) return;
                  const availableFields = existingFields.map((f) => {
                    const cfg = (f.config ?? {}) as Record<string, unknown>;
                    const formula = typeof cfg.formula === 'string' ? cfg.formula : undefined;
                    return { slug: f.slug, name: f.name, type: f.fieldType, formula };
                  });
                  const currentFormula = String(form.getFieldValue('formula') ?? '');
                  const historySnapshot = chatMessages
                    .filter((m) => m.kind !== 'error')
                    .map((m) => ({ role: m.role, content: m.role === 'assistant' && m.formula ? m.formula : m.content }));
                  setChatMessages((prev) => [...prev, { role: 'user', content: text }]);
                  setAiPrompt('');
                  setAiLoading(true);
                  try {
                    const result = await suggestFormula(
                      text,
                      availableFields,
                      currentFormula,
                      historySnapshot,
                      initialFormula || undefined,
                    );
                    form.setFieldValue('formula', result.formula);
                    setChatMessages((prev) => [
                      ...prev,
                      { role: 'assistant', content: 'Готово, формула обновлена', formula: result.formula, kind: 'suggest' },
                    ]);
                  } catch {
                    setChatMessages((prev) => [
                      ...prev,
                      { role: 'assistant', content: 'Не удалось сгенерировать формулу', kind: 'error' },
                    ]);
                  } finally {
                    setAiLoading(false);
                  }
                }}
                onExplain={async () => {
                  const formulaValue = String(form.getFieldValue('formula') ?? '').trim();
                  if (!formulaValue) {
                    void message.warning('Введите формулу, чтобы её объяснить');
                    return;
                  }
                  if (aiLoading) return;
                  const availableFields = existingFields.map((f) => {
                    const cfg = (f.config ?? {}) as Record<string, unknown>;
                    const formula = typeof cfg.formula === 'string' ? cfg.formula : undefined;
                    return { slug: f.slug, name: f.name, type: f.fieldType, formula };
                  });
                  setChatMessages((prev) => [...prev, { role: 'user', content: 'Объясни эту формулу' }]);
                  setAiLoading(true);
                  try {
                    const result = await explainFormula(formulaValue, availableFields);
                    setChatMessages((prev) => [
                      ...prev,
                      { role: 'assistant', content: result.explanation, kind: 'explain' },
                    ]);
                  } catch {
                    setChatMessages((prev) => [
                      ...prev,
                      { role: 'assistant', content: 'Не удалось получить объяснение.', kind: 'error' },
                    ]);
                  } finally {
                    setAiLoading(false);
                  }
                }}
                onClear={() => setChatMessages([])}
              />
            </Form.Item>
          </>
        )}

      </Form>
    </Modal>
  );
}

// ─── ItemTypesTab ─────────────────────────────────────────────────────────────

export function ItemTypesTab({ spaceId, myRole, jiraEnabled = false }: ItemTypesTabProps): JSX.Element {
  const isAdmin = myRole === 'admin';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemTypes, setItemTypes] = useState<ItemTypeListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<ItemType | null>(null);
  const [loadingFields, setLoadingFields] = useState(false);

  // Modals
  const [createTypeOpen, setCreateTypeOpen] = useState(false);
  const [fromJiraOpen, setFromJiraOpen] = useState(false);
  const [addFieldsFromJiraOpen, setAddFieldsFromJiraOpen] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldDefinition | null>(null);

  // Rename item type
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameIcon, setRenameIcon] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const handleRenameType = async (typeId: string): Promise<void> => {
    if (!renameValue.trim()) return;
    setSavingRename(true);
    try {
      await updateItemType(spaceId, typeId, {
        name: renameValue.trim(),
        icon: renameIcon || null,
      });
      setRenamingId(null);
      await loadItemTypes();
    } catch {
      /* ignore */
    } finally {
      setSavingRename(false);
    }
  };

  // Load item types list
  const loadItemTypes = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const list = await listItemTypes(spaceId);
      setItemTypes(list);
      const first = list[0];
      if (first && !selectedId) {
        setSelectedId(first.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [spaceId, selectedId]);

  useEffect(() => { void loadItemTypes(); }, [spaceId]);

  // Load full item type when selected
  useEffect(() => {
    if (!selectedId) { setSelectedType(null); return; }
    setLoadingFields(true);
    getItemType(spaceId, selectedId)
      .then((it) => setSelectedType(it))
      .catch(() => setSelectedType(null))
      .finally(() => setLoadingFields(false));
  }, [spaceId, selectedId]);

  const handleDeleteType = async (id: string): Promise<void> => {
    await deleteItemType(spaceId, id);
    if (selectedId === id) { setSelectedId(null); setSelectedType(null); }
    await loadItemTypes();
  };

  const handleDuplicateType = async (id: string): Promise<void> => {
    try {
      const created = await duplicateItemType(spaceId, id);
      message.success(`Тип «${created.name}» создан`);
      await loadItemTypes();
      setSelectedId(created.id);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Не удалось продублировать тип');
    }
  };

  const handleDeleteField = async (fieldId: string): Promise<void> => {
    if (!selectedId) return;
    await deleteField(spaceId, selectedId, fieldId);
    if (selectedType) {
      setSelectedType({
        ...selectedType,
        fields: selectedType.fields.filter((f) => f.id !== fieldId),
      });
    }
  };

  const handleFieldSaved = (field: FieldDefinition): void => {
    setFieldModalOpen(false);
    setEditingField(null);
    if (!selectedType) return;
    const exists = selectedType.fields.some((f) => f.id === field.id);
    setSelectedType({
      ...selectedType,
      fields: exists
        ? selectedType.fields.map((f) => (f.id === field.id ? field : f))
        : [...selectedType.fields, field],
    });
  };

  const fieldDndSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const fieldDndSensors = useSensors(fieldDndSensor);

  const handleFieldDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedType) return;
    const oldIndex = selectedType.fields.findIndex((f) => f.id === active.id);
    const newIndex = selectedType.fields.findIndex((f) => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newFields = arrayMove(selectedType.fields, oldIndex, newIndex);
    setSelectedType({ ...selectedType, fields: newFields });
    try {
      const result = await reorderFields(spaceId, selectedType.id, { orderedIds: newFields.map((f) => f.id) });
      setSelectedType((prev) => prev ? { ...prev, fields: result } : prev);
    } catch { /* rollback handled by next render */ }
  }, [selectedType, spaceId]);

  const fieldColumns: ColumnsType<FieldDefinition> = [
    ...(isAdmin
      ? [
          {
            title: '',
            key: 'drag',
            width: 36,
            render: () => (
              <HolderOutlined style={{ cursor: 'grab', color: 'var(--color-text-secondary)' }} />
            ),
          } as ColumnsType<FieldDefinition>[0],
        ]
      : []),
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, rec) => (
        <Space>
          <span>{name}</span>
          {(rec.config as { hasCategories?: boolean })?.hasCategories && (
            <Tag
              bordered={false}
              style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)', borderRadius: radius.sm }}
            >
              Статусное
            </Tag>
          )}
          {rec.required && (
            <Tag
              bordered={false}
              style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)', borderRadius: radius.sm }}
            >
              Обязательное
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <Text code>{slug}</Text>,
    },
    {
      title: 'Тип',
      dataIndex: 'fieldType',
      key: 'fieldType',
      render: (ft: string) => FIELD_TYPE_LABELS[ft] ?? ft,
    },
    ...(isAdmin
      ? [
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_: unknown, record: FieldDefinition) => (
              <Space size={0}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setEditingField(record); setFieldModalOpen(true); }}
                />
                <Popconfirm
                  title="Удалить поле?"
                  description="Значения во всех записях будут удалены."
                  onConfirm={() => handleDeleteField(record.id)}
                  okText="Удалить"
                  cancelText="Отмена"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          } as ColumnsType<FieldDefinition>[0],
        ]
      : []),
  ];

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />;
  }
  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return (
    <Row gutter={16} style={{ minHeight: 400 }}>
      {/* Left: item types list */}
      <Col span={6} style={{ borderRight: `1px solid var(--color-border)`, paddingRight: spacing.base }}>
        <div style={{ marginBottom: spacing.md, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>Типы записей</Title>
          {isAdmin && (
            <Space size={4} wrap>
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setCreateTypeOpen(true)}
              >
                Добавить
              </Button>
              {jiraEnabled && (
                <Button
                  type="dashed"
                  size="small"
                  onClick={() => setFromJiraOpen(true)}
                  title="Создать тип из примера задачи Jira"
                >
                  Из Jira
                </Button>
              )}
            </Space>
          )}
        </div>

        {itemTypes.length === 0 ? (
          <Text type="secondary">Нет типов записей. Создайте первый.</Text>
        ) : (
          itemTypes.map((it) => (
            <div
              key={it.id}
              onClick={() => setSelectedId(it.id)}
              style={{
                padding: '8px 12px',
                marginBottom: spacing.xs,
                borderRadius: radius.sm,
                cursor: 'pointer',
                background: selectedId === it.id ? 'var(--color-primary-bg)' : 'transparent',
                border: selectedId === it.id ? '1px solid var(--color-primary)' : '1px solid transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {renamingId === it.id ? (
                <Space size={4} onClick={(e) => e.stopPropagation()}>
                  <EmojiPicker
                    value={renameIcon || null}
                    onChange={(v) => setRenameIcon(v ?? '')}
                  />
                  <Input
                    size="small"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleRenameType(it.id); if (e.key === 'Escape') setRenamingId(null); }}
                    autoFocus
                    style={{ width: 120 }}
                  />
                  <Button size="small" type="text" icon={<CheckOutlined />} loading={savingRename} onClick={(e) => { e.stopPropagation(); void handleRenameType(it.id); }} />
                  <Button size="small" type="text" icon={<CloseOutlined />} onClick={(e) => { e.stopPropagation(); setRenamingId(null); }} />
                </Space>
              ) : (
                <>
                  <Space>
                    {it.icon && <span>{it.icon}</span>}
                    <span>{it.name}</span>
                    <Badge count={it.fieldCount} showZero color="var(--color-text-secondary)" size="small" />
                  </Space>
                  {isAdmin && (
                    <Space size={0}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => { e.stopPropagation(); setRenamingId(it.id); setRenameValue(it.name); setRenameIcon(it.icon ?? ''); }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        title="Дублировать тип"
                        onClick={(e) => { e.stopPropagation(); void handleDuplicateType(it.id); }}
                      />
                      <Popconfirm
                        title="Удалить тип записи?"
                        description="Все записи этого типа будут удалены."
                        onConfirm={() => handleDeleteType(it.id)}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </Space>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </Col>

      {/* Right: fields list */}
      <Col span={18}>
        {!selectedId ? (
          <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--color-text-secondary)' }}>
            <UnorderedListOutlined style={{ fontSize: 32, marginBottom: 8 }} />
            <div>Выберите тип записи</div>
          </div>
        ) : loadingFields ? (
          <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
        ) : (
          <>
            <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
              <Title level={5} style={{ margin: 0 }}>
                {selectedType?.name ?? ''} — поля
              </Title>
              {isAdmin && (
                <Space size={4}>
                  {jiraEnabled && (
                    <Button
                      size="small"
                      onClick={() => setAddFieldsFromJiraOpen(true)}
                      title="Подтянуть новые поля из примера задачи Jira"
                    >
                      Из Jira…
                    </Button>
                  )}
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => { setEditingField(null); setFieldModalOpen(true); }}
                  >
                    Добавить поле
                  </Button>
                </Space>
              )}
            </Space>
            <DndContext sensors={fieldDndSensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleFieldDragEnd(e)}>
              <SortableContext items={(selectedType?.fields ?? []).map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <Table<FieldDefinition>
                  dataSource={selectedType?.fields ?? []}
                  columns={fieldColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'Нет полей' }}
                  components={{
                    body: {
                      row: SortableFieldRow,
                    },
                  }}
                  onRow={(record) => ({ 'data-row-key': record.id } as React.HTMLAttributes<HTMLTableRowElement>)}
                />
              </SortableContext>
            </DndContext>
            {selectedType && (
              <LinkRulesPanel
                spaceId={spaceId}
                itemType={selectedType}
                allItemTypes={itemTypes}
                myRole={myRole}
              />
            )}
          </>
        )}
      </Col>

      <CreateItemTypeModal
        spaceId={spaceId}
        open={createTypeOpen}
        onCancel={() => setCreateTypeOpen(false)}
        onCreated={(it) => {
          setCreateTypeOpen(false);
          void loadItemTypes().then(() => setSelectedId(it.id));
        }}
      />

      <CreateItemTypeFromJiraWizard
        spaceId={spaceId}
        open={fromJiraOpen}
        onCancel={() => setFromJiraOpen(false)}
        onCreated={(itemTypeId) => {
          setFromJiraOpen(false);
          void loadItemTypes().then(() => setSelectedId(itemTypeId));
        }}
      />

      {selectedId && selectedType && (
        <AddFieldsFromJiraWizard
          spaceId={spaceId}
          itemTypeId={selectedId}
          itemTypeName={selectedType.name}
          open={addFieldsFromJiraOpen}
          onCancel={() => setAddFieldsFromJiraOpen(false)}
          onAdded={() => {
            setAddFieldsFromJiraOpen(false);
            // Перечитываем тип с полями.
            void getItemType(spaceId, selectedId).then((it) => setSelectedType(it));
          }}
        />
      )}

      {selectedId && (
        <FieldBuilderModal
          spaceId={spaceId}
          itemTypeId={selectedId}
          open={fieldModalOpen}
          editingField={editingField}
          existingFields={selectedType?.fields ?? []}
          onCancel={() => { setFieldModalOpen(false); setEditingField(null); }}
          onSaved={handleFieldSaved}
        />
      )}
    </Row>
  );
}
